import { StateCreator } from 'zustand';

interface ReservationOtpStoreHandlers {
  setReservationOtpVerified: (verified: ReservationOtpVerified | null) => void;
  setReservationOtpCodeSent: (email: string, resendAvailableAt: number) => void;
  setReservationOtpResendAvailableAt: (email: string, resendAvailableAt: number) => void;
  setReservationOtpCodeUsed: (email: string) => void;
  setReservationOtpRequiredByBackend: () => void;
  resetReservationOtp: () => void;
}

export type ReservationOtpVerified = {
  /** Normalized booking email the token was issued for. */
  email: string;
  otpToken: string;
};

export type ReservationOtpSentCode = {
  /** Time (ms) from which a new code can be requested. */
  resendAvailableAt: number;
  /** Verified already: the backend deleted it, so it cannot be entered again even while the resend is still in cooldown. */
  isUsed: boolean;
};

interface ReservationOtpFields {
  /** The backend releases the verification when the booking fails, so a retry with the same email can reuse it. */
  reservationOtpVerified: ReservationOtpVerified | null;
  /** Last code sent to each normalized email from this tab. */
  reservationOtpSentCodes: Record<string, ReservationOtpSentCode>;
  /** The backend refused a booking without OTP the frontend rule had skipped: from then on it is asked for every payment without charge. */
  isReservationOtpRequiredByBackend: boolean;
}

export interface ReservationOtpState extends ReservationOtpStoreHandlers, ReservationOtpFields {}

// Typed as the full field set: a new field does not compile until it has an initial value, and so a non-persisted key.
const initialReservationOtpFields: ReservationOtpFields = {
  reservationOtpVerified: null,
  reservationOtpSentCodes: {},
  isReservationOtpRequiredByBackend: false,
};

/**
 * Kept in the store, not in the payment widget: the widget unmounts when the visitor goes back to the information step,
 * and coming back must neither send a new email nor forget a verified email.
 * Never persisted: the token is a proof of identity, it must not outlive the tab.
 * Derived from the initial fields, so a field added later cannot be forgotten here.
 */
export const RESERVATION_OTP_STATE_KEYS = Object.keys(initialReservationOtpFields) as (keyof ReservationOtpFields)[];

export const createReservationOtpSlice: StateCreator<ReservationOtpState, [], [], ReservationOtpState> = set => ({
  ...initialReservationOtpFields,

  setReservationOtpVerified: (verified: ReservationOtpVerified | null): void => set({ reservationOtpVerified: verified }),

  setReservationOtpCodeSent: (email: string, resendAvailableAt: number): void =>
    set(state => ({ reservationOtpSentCodes: { ...state.reservationOtpSentCodes, [email]: { resendAvailableAt, isUsed: false } } })),

  // A refused send (5008) only moves the cooldown: the code it refers to keeps its state.
  setReservationOtpResendAvailableAt: (email: string, resendAvailableAt: number): void =>
    set(state => ({
      reservationOtpSentCodes: {
        ...state.reservationOtpSentCodes,
        [email]: { resendAvailableAt, isUsed: state.reservationOtpSentCodes[email]?.isUsed ?? false },
      },
    })),

  setReservationOtpCodeUsed: (email: string): void =>
    set(state => {
      const sentCode = state.reservationOtpSentCodes[email];
      if (!sentCode) return {};
      return { reservationOtpSentCodes: { ...state.reservationOtpSentCodes, [email]: { ...sentCode, isUsed: true } } };
    }),

  setReservationOtpRequiredByBackend: (): void => set({ isReservationOtpRequiredByBackend: true }),

  resetReservationOtp: (): void => set(initialReservationOtpFields),
});
